/* eslint-disable no-undef */
import { Texts, Language, TextGroup, Languages } from '../@types';
import { isWeb } from './platformHelpers';

type Dictionary = Texts & {
  group: TextGroup;
  language: Language;
};

export const isReportingMissingTextsEnabled = (userId: string): boolean => {
  const randomHex = Math.floor(Math.random() * 16 + 1).toString(16);
  return userId?.endsWith(randomHex);
};

export const interpolateVariables = (
  template: string,
  params: Array<string>
): string => {
  const matcher = /(%s|%@|%d)/;
  return params.reduce(
    (stringBuilder, param) =>
      stringBuilder.replace(matcher, param != null ? String(param) : ''),
    template
  );
};
/**
 * Optional trailing options for `localize`. `fallback` is rendered when the key
 * is missing from the dictionary (and the backend hasn't supplied it yet), so a
 * missing key never shows the raw key string — works on every brand without
 * per-brand InitialState edits. Mirrors react-intl's `defaultMessage`.
 */
export type LocalizeOptions = { fallback?: string };

const isLocalizeOptions = (value: unknown): value is LocalizeOptions =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  'fallback' in (value as object);

export const localize = (
  dictionary: Dictionary,
  uniqueId: string,
  key: string = '',
  ...params: Array<string | number | LocalizeOptions>
) => {
  // A trailing { fallback } object may follow the interpolation params.
  let fallback: string | undefined;
  let interpolationParams = params as Array<string>;
  const last = params[params.length - 1];
  if (isLocalizeOptions(last)) {
    fallback = last.fallback;
    interpolationParams = params.slice(0, -1) as Array<string>;
  }
  const onMissing = () =>
    fallback != null
      ? interpolateVariables(fallback, interpolationParams)
      : key;

  if (
    !dictionary ||
    !dictionary?.group ||
    !dictionary?.language ||
    !dictionary?.[dictionary?.group]
  ) {
    // @ts-ignore
    if (!isWeb() && __DEV__)
      // eslint-disable-next-line no-console
      console.error('Localization dictionary is not found');
    return onMissing();
  }
  const languages: Languages = dictionary?.[dictionary?.group];
  const values = languages?.[dictionary?.language];
  const value = values?.[key];

  // Ensure value is a string - if it's a React element or object, return the key
  if (value && typeof value !== 'string') {
    console.error(
      `Localization value for key "${key}" is not a string:`,
      typeof value
    );
    return onMissing();
  }

  const localization =
    value && interpolateVariables(value, interpolationParams);

  if (!localization) {
    // @ts-ignore
    if (!isWeb() && __DEV__) {
      if (key === '') {
        // eslint-disable-next-line no-console
        console.error('No text key provided to localization function');
      } else {
        // const consoleMethod = injectedDimensions?.get('window')
        //   ?.showLocalizationWarnings
        //   ? 'warn'
        //   : 'log';
        // eslint-disable-next-line no-console
        // console[consoleMethod](`Localization not found for text key "${key}"`);
      }
    }
    // else if (isReportingMissingTextsEnabled(uniqueId)) {
    // injectedSentry?.captureException(
    //   `Localization not found for text key "${key}"`
    // );
    // }

    return onMissing();
  }

  return localization;
};
